import { useEffect, useMemo, useState } from 'react';
import { useOutreachHandleImport } from '@discovr/features';
import { creatorKey, handleKey, useCampaignPicks, useDiscoveryCampaign } from '@discovr/features/discovery';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../../components/ui/dialog';
import { Skeleton } from '../../../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { NO_VALUE } from '../../../discovery/constants/discoveryCopy';
import { OUTREACH_PULL_COPY, POOL_COPY, PULL_DIALOG_COPY as PULL } from '../../../discovery/constants/findCopy';
import { ConfirmDialog } from '../../../discovery/parts/ConfirmDialog';
import { plural } from '../../../discovery/discoveryText';
import { countText } from '../../../discovery/format';
import {
  checksPassed,
  fitScore,
  handleOf,
  matchesChip,
  nameOf,
  outreachHandles,
  PULL_CHIPS,
  storedEmail,
  type EmailMap,
  type PoolCreator,
  type PullChip,
} from '../../../discovery/poolModel';
import { pickMap } from '../../../discovery/results/resultsModel';
import { previewSummary, usePoolOutreach } from '../../../discovery/usePoolOutreach';
import { cn } from '../../../lib/utils';

const COPY = POOL_COPY.outreach;
const IN_OUTREACH = new Set(['already_in_campaign', 'has_price']);
const ER_DECIMALS = 2;
const COLUMN_WIDTH = { select: 44, creator: 280, email: 250, followers: 100, avgViews: 100, er: 80, fit: 110 } as const;
const width = (key: keyof typeof COLUMN_WIDTH) => ({ width: COLUMN_WIDTH[key] });

/** Instagram creators with a handle: the only ones outreach can take. */
export const pullableCreators = (pool: readonly PoolCreator[]): PoolCreator[] =>
  pool.filter((creator) => outreachHandles([creator]).length > 0);

const erText = (creator: PoolCreator) =>
  creator.erPct == null ? NO_VALUE : `${creator.erPct.toFixed(ER_DECIMALS)}%`;

interface OutreachPullDialogProps {
  open: boolean;
  campaignId: string;
  onClose: () => void;
}

export function OutreachPullDialog({ open, campaignId, onClose }: OutreachPullDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="flex max-h-[85vh] w-[1070px] flex-col gap-0 overflow-hidden rounded-2xl p-0 sm:max-w-[min(1070px,calc(100vw-2rem))]">
        {open ? <PullBody campaignId={campaignId} onClose={onClose} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function PullBody({ campaignId, onClose }: { campaignId: string; onClose: () => void }) {
  const detail = useDiscoveryCampaign(campaignId);
  const picksQuery = useCampaignPicks(campaignId);
  const [chip, setChip] = useState<PullChip>('all');
  const [selection, setSelection] = useState<ReadonlySet<string>>(new Set());
  const outreach = usePoolOutreach(campaignId, onClose);
  const { preview: statusLookup } = useOutreachHandleImport(campaignId);
  const [lookup, setLookup] = useState<{ key: string; statuses: Record<string, string>; emails: EmailMap } | null>(null);
  const [failedKey, setFailedKey] = useState<string | null>(null);

  const picks = pickMap(picksQuery.data?.picks);
  const pullable = useMemo(
    () => pullableCreators([...(detail.data?.pool ?? [])].sort((a, b) => (b.fit ?? 0) - (a.fit ?? 0))),
    [detail.data],
  );
  const pullableKey = pullable.map(creatorKey).join('|');

  const lookUp = () => {
    const handles = outreachHandles(pullable);
    if (!handles.length) return;
    const key = pullableKey;
    statusLookup.mutate(handles, {
      onSuccess: (result) => {
        setLookup({ key, statuses: result.statuses ?? {}, emails: result.contactEmails ?? {} });
        setFailedKey(null);
      },
      onError: () => setFailedKey(key),
    });
  };

  useEffect(() => {
    lookUp();
    // The lookup is keyed on the pool's handles, not on the mutation object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pullableKey]);

  const fresh = lookup?.key === pullableKey ? lookup : null;
  const statuses = fresh?.statuses ?? {};
  const emails = fresh?.emails ?? {};
  const looked = fresh !== null;
  const lookupFailed = failedKey === pullableKey;

  const inOutreach = (creator: PoolCreator) =>
    IN_OUTREACH.has(statuses[handleKey(outreachHandles([creator])[0] ?? '')] ?? '');
  const rows = pullable.filter((creator) => matchesChip(creator, chip, emails, picks));
  const chipCount = (id: PullChip) => pullable.filter((creator) => matchesChip(creator, id, emails, picks)).length;
  const chosen = pullable.filter((creator) => selection.has(creatorKey(creator)) && !inOutreach(creator));
  const chosenWithoutEmail = chosen.filter((creator) => storedEmail(creator, emails) === null).length;
  const summary = outreach.previewed
    ? { ...previewSummary(outreach.previewed), title: OUTREACH_PULL_COPY.title(plural(outreach.previewed.counts.ok, 'creator')) }
    : null;
  const blocked = outreach.blockedReason(chosen);
  const toggle = (creator: PoolCreator) =>
    setSelection((prev) => {
      const next = new Set(prev);
      const key = creatorKey(creator);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const emptyText =
    pullable.length === 0 ? PULL.empty.all : chip === 'shortlisted' ? PULL.empty.shortlisted : PULL.empty.filtered;

  return (
    <>
      <div className="px-6 pt-5">
        <DialogTitle className="text-[18px] font-medium leading-[26px]">{PULL.title}</DialogTitle>
        <DialogDescription className="mt-1 text-[14px] text-foreground-muted">{PULL.subtitle}</DialogDescription>
        <div className="mt-3 flex gap-2" role="tablist">
          {PULL_CHIPS.map(({ chip: id, label }) => (
            <Button
              key={id}
              type="button"
              role="tab"
              aria-selected={chip === id}
              size="sm"
              variant={chip === id ? 'default' : 'outline'}
              disabled={(id === 'email' || id === 'noEmail') && !looked}
              onClick={() => setChip(id)}
            >
              {label}
              {id === 'email' || id === 'noEmail' ? (looked ? ` ${chipCount(id)}` : '') : ` ${chipCount(id)}`}
            </Button>
          ))}
        </div>
      </div>
      <div className="mt-3 min-h-[160px] flex-1 overflow-y-auto border-t border-stone-200">
        {detail.isPending ? (
          <Skeleton className="m-6 h-24 w-[calc(100%-3rem)]" />
        ) : detail.isError ? (
          <p className="p-6 text-sm text-foreground-muted">{PULL.loadFailed}</p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-sm text-foreground-muted">{emptyText}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead style={width('select')} />
                <TableHead style={width('creator')}>{PULL.columns.creator}</TableHead>
                <TableHead style={width('email')}>{PULL.columns.email}</TableHead>
                <TableHead style={width('followers')} className="text-right">{PULL.columns.followers}</TableHead>
                <TableHead style={width('avgViews')} className="text-right">{PULL.columns.avgViews}</TableHead>
                <TableHead style={width('er')} className="text-right">{PULL.columns.er}</TableHead>
                <TableHead style={width('fit')} className="text-right">{PULL.columns.fit}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((creator) => {
                const taken = inOutreach(creator);
                const key = creatorKey(creator);
                const email = storedEmail(creator, emails);
                const fit = fitScore(creator);
                return (
                  <TableRow key={key} className={cn(taken && 'opacity-60')}>
                    <TableCell style={width('select')}>
                      <Checkbox
                        checked={selection.has(key) && !taken}
                        disabled={taken}
                        aria-label={`Select ${nameOf(creator)}`}
                        onCheckedChange={() => toggle(creator)}
                      />
                    </TableCell>
                    <TableCell style={width('creator')}>
                      <span className="flex items-center gap-3">
                        <Avatar size="lg">
                          {creator.avatarUrl ? <AvatarImage src={creator.avatarUrl} alt="" /> : null}
                          <AvatarFallback>{nameOf(creator).slice(0, 1).toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <span className="min-w-0">
                          <span className="block truncate text-sm">{nameOf(creator)}</span>
                          <span className="block truncate text-xs text-foreground-muted">@{handleOf(creator)}</span>
                        </span>
                        {taken ? <span className="shrink-0 text-xs text-foreground-muted">{PULL.taken}</span> : null}
                      </span>
                    </TableCell>
                    <TableCell style={width('email')} className="truncate">
                      {email ??
                        (lookupFailed ? (
                          <span className="text-foreground-muted">
                            {PULL.lookupFailed}{' '}
                            <button
                              type="button"
                              className="underline"
                              onClick={() => {
                                setFailedKey(null);
                                lookUp();
                              }}
                            >
                              {PULL.retry}
                            </button>
                          </span>
                        ) : (
                          <span className="text-foreground-muted">{looked ? PULL.emailMissing : NO_VALUE}</span>
                        ))}
                    </TableCell>
                    <TableCell style={width('followers')} numeric>
                      {countText(creator.followers ?? null)}
                    </TableCell>
                    <TableCell style={width('avgViews')} numeric>
                      {countText(creator.avgViews ?? null)}
                    </TableCell>
                    <TableCell style={width('er')} numeric>
                      {erText(creator)}
                    </TableCell>
                    <TableCell style={width('fit')} numeric>
                      {fit === null ? NO_VALUE : fit}
                      <span className="ml-1 text-xs text-foreground-muted">{checksPassed(creator)}</span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
      <div className="flex items-center gap-3 border-t border-stone-200 px-6 py-4">
        <span className="text-sm">{PULL.selected(chosen.length)}</span>
        {chosenWithoutEmail > 0 ? (
          <span className="text-xs text-foreground-muted">{PULL.noEmail(chosenWithoutEmail)}</span>
        ) : null}
        {blocked && chosen.length > 0 ? <span className="text-xs text-foreground-muted">{blocked}</span> : null}
        <span className="flex-1" />
        <Button variant="outline" onClick={onClose}>
          {PULL.cancel}
        </Button>
        <Button
          disabled={chosen.length === 0 || blocked !== null}
          loading={outreach.previewing}
          onClick={() => outreach.start(chosen)}
        >
          {PULL.add(chosen.length)}
        </Button>
      </div>
      <ConfirmDialog
        open={summary !== null}
        title={summary?.title ?? ''}
        body={summary?.body ?? ''}
        confirmLabel={OUTREACH_PULL_COPY.confirm}
        cancelLabel={COPY.cancel}
        confirmVariant="default"
        pending={outreach.committing}
        onConfirm={outreach.confirm}
        onCancel={outreach.cancel}
      />
    </>
  );
}
